/**
 * Het taakvoorstel uit een antwoord halen. Post 10b-glas.
 *
 * **HETZELFDE PATROON ALS `agendablok.js`, EN DAT IS EEN EIS.** Franks opdracht
 * van 12 september 2026: volg de bedrading van de mailbevestiging, geen tweede
 * bevestigingsvorm.
 *
 * **HET BLOK DRAAGT EEN ID EN GEEN VELDEN, EN DAAR ZIT DE HELE VEILIGHEID.**
 * Zou het de titel, de werkruimte en de deadline dragen, dan leest dit bestand
 * ze eruit en toont het scherm ze op de kaart - en dan bevestigt Frank wat het
 * BLOK zegt. Maar een blok is tekst in een bericht, en tekst in een bericht is
 * precies wat een taalmodel kan schrijven.
 *
 * Met alleen een id haalt het scherm de velden bij de server op, uit dezelfde
 * rij die de uitvoerroute vergelijkt. Dezelfde redenering als bij
 * `MailAntwoord`: wat in de naleesstap staat komt van de server terug en niet
 * uit het scherm.
 *
 * **HET IS EEN PURE FUNCTIE**, zodat de beslissing op UITKOMST te toetsen is in
 * plaats van op de vraag of er toevallig iets in de DOM staat.
 */

// Moet gelijk blijven aan `MERK` in backend/services/taakblok.py.
// Er is aan beide kanten een wachter die dat afdwingt.
export const MERK = "\n\n---\n[taakvoorstel]";

// Moet gelijk blijven aan `KEUZE` in dezelfde module.
export const KEUZE = "keuze";

// Post 10c stap 3: een wijziging van een BESTAANDE taak. Moeten gelijk blijven
// aan `WIJZIGING` en `WIJZIGKEUZE` in backend/services/taakblok.py.
export const WIJZIGING = "wijziging";
export const WIJZIGKEUZE = "wijzigkeuze";
// C2a, 15 oktober 2026: een afspraakvoorstel in hetzelfde blok.
export const AFSPRAAK = "afspraak";
// DW1, 24 oktober 2026: een voorstel voor een digitale worker. Moet gelijk blijven aan
// `WORKER` in backend/services/taakblok.py.
export const WORKER = "worker";
// CA5, 1 november 2026: een app bouwen vanuit de chat. Moet gelijk blijven aan `APP` in
// backend/services/taakblok.py.
export const APP = "app";

const SCHEIDING = " :: ";

/**
 * Ontleedt het blok NA het merkteken. Geeft null als het er niet uitziet zoals
 * wij het schrijven; dan valt de aanroeper terug op platte tekst, precies zoals
 * `agendablok.ontleed` doet.
 */
export function ontleed(blok) {
  const regel = (blok || "").split("\n").find((r) => r.trim()) || "";
  const kaal = regel.trim();
  if (!kaal) return null;
  // EERST DE WIJZIGVORMEN, want die dragen een scheidingsteken en zouden
  // hieronder anders als onleesbaar terugvallen op platte tekst.
  if (kaal.startsWith(WIJZIGKEUZE + SCHEIDING)) {
    const zin = kaal.slice((WIJZIGKEUZE + SCHEIDING).length).trim();
    return zin ? { soort: "wijziging", keuze: true, zin } : null;
  }
  if (kaal.startsWith(WORKER + SCHEIDING)) {
    const id = kaal.slice((WORKER + SCHEIDING).length).trim();
    return id && !/\s/.test(id) ? { soort: "worker", keuze: false, id } : null;
  }
  if (kaal.startsWith(APP + SCHEIDING)) {
    const id = kaal.slice((APP + SCHEIDING).length).trim();
    return id && !/\s/.test(id) ? { soort: "app", keuze: false, id } : null;
  }
  if (kaal.startsWith(AFSPRAAK + SCHEIDING)) {
    const id = kaal.slice((AFSPRAAK + SCHEIDING).length).trim();
    return id && !/\s/.test(id) ? { soort: "afspraak", keuze: false, id } : null;
  }
  if (kaal.startsWith(WIJZIGING + SCHEIDING)) {
    const id = kaal.slice((WIJZIGING + SCHEIDING).length).trim();
    return id && !/\s/.test(id) ? { soort: "wijziging", keuze: false, id } : null;
  }
  if (kaal.startsWith(KEUZE + SCHEIDING)) {
    const zin = kaal.slice((KEUZE + SCHEIDING).length).trim();
    // EEN KEUZEVRAAG ZONDER ZIN IS ONBRUIKBAAR: na de klik moet dezelfde zin
    // opnieuw worden gestuurd met de gekozen naam erbij. Zonder zin is er
    // niets om opnieuw te sturen, en dan is de knop dood.
    return zin ? { keuze: true, zin } : null;
  }
  // EEN ID EN NIETS ANDERS. Een regel met een scheidingsteken erin is onze
  // voorstelregel niet; hem toch als id lezen zou een 404 opleveren waar een
  // terugval op platte tekst hoorde te staan.
  if (kaal.includes(SCHEIDING) || /\s/.test(kaal)) return null;
  return { keuze: false, id: kaal };
}

/**
 * Splitst een bericht in het gewone antwoord en het taakvoorstel.
 * Geen merkteken of onleesbaar blok: alles blijft tekst, precies zoals het was.
 */
export function splits(content) {
  const heel = content || "";
  const i = heel.indexOf(MERK);
  if (i === -1) return { tekst: heel, taak: null };
  const taak = ontleed(heel.slice(i + MERK.length));
  if (!taak) return { tekst: heel, taak: null };
  return { tekst: heel.slice(0, i), taak };
}
